import React, { useState, useMemo } from 'react';
import Table from '../../components/Table/Table';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import CandidateForm from '../../components/HR/CandidateForm';
import CandidateDetailsModal from '../../components/HR/CandidateDetailsModal';
import { Candidate } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useCandidates, useCreateCandidate, useUpdateCandidate, useDeleteCandidate } from '../../hooks/useCandidates';
import { PageHeader, StatsCards, FilterBar, ErrorState } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';
import { getCandidateColumns } from './CandidateColumns';

const Candidates: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState<Candidate | undefined>();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 },
  }), []);

  const { data: candidatesData, isLoading, error, refetch } = useCandidates(queryOptions);
  const candidates = useMemo(() => candidatesData || [], [candidatesData]);

  const createMutation = useCreateCandidate(() => { setShowForm(false); setEditingCandidate(undefined); setTimeout(() => refetch(), 100); });
  const updateMutation = useUpdateCandidate(() => { setShowForm(false); setEditingCandidate(undefined); setTimeout(() => refetch(), 100); });
  const deleteMutation = useDeleteCandidate(() => { setTimeout(() => refetch(), 100); });

  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [nationalityFilter, setNationalityFilter] = useState('all');
  const [experienceFilter, setExperienceFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');

  const uniquePositions = useMemo(() => {
    if (!candidates || candidates.length === 0) return [];
    return Array.from(new Set(candidates.map(c => c.appliedPosition).filter(Boolean)));
  }, [candidates]);

  const uniqueNationalities = useMemo(() => {
    if (!candidates || candidates.length === 0) return [];
    return Array.from(new Set(candidates.map(c => c.nationality).filter(Boolean)));
  }, [candidates]);

  const filteredCandidates = useMemo(() => {
    if (!candidates || candidates.length === 0) return [];
    return candidates.filter(candidate => {
      const matchesSearch = !searchTerm || (
        candidate.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.appliedPosition.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.nationality.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (candidate.assignedManagerName && candidate.assignedManagerName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      const matchesPosition = positionFilter === 'all' || candidate.appliedPosition === positionFilter;
      const matchesStatus = statusFilter === 'all' || candidate.status === statusFilter;
      const matchesSource = sourceFilter === 'all' || candidate.source === sourceFilter;
      const matchesNationality = nationalityFilter === 'all' || candidate.nationality === nationalityFilter;
      const matchesExperience = experienceFilter === 'all' ||
        (experienceFilter === '0-2' && candidate.experience <= 2) ||
        (experienceFilter === '3-5' && candidate.experience >= 3 && candidate.experience <= 5) ||
        (experienceFilter === '6-10' && candidate.experience >= 6 && candidate.experience <= 10) ||
        (experienceFilter === '10+' && candidate.experience > 10);
      const matchesRating = ratingFilter === 'all' ||
        (ratingFilter === '0' && candidate.totalRating === 0) ||
        (ratingFilter === '1-3' && candidate.totalRating >= 1 && candidate.totalRating <= 3) ||
        (ratingFilter === '4-6' && candidate.totalRating >= 4 && candidate.totalRating <= 6) ||
        (ratingFilter === '7-8' && candidate.totalRating >= 7 && candidate.totalRating <= 8) ||
        (ratingFilter === '9-10' && candidate.totalRating >= 9 && candidate.totalRating <= 10);
      const appliedDate = new Date(candidate.appliedDate);
      const matchesDateFrom = !dateFromFilter || appliedDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || appliedDate <= new Date(dateToFilter);

      return matchesSearch && matchesPosition && matchesStatus && matchesSource &&
             matchesNationality && matchesExperience && matchesRating && matchesDateFrom && matchesDateTo;
    });
  }, [candidates, searchTerm, positionFilter, statusFilter, sourceFilter, nationalityFilter, experienceFilter, ratingFilter, dateFromFilter, dateToFilter]);

  const buildExportData = () => filteredCandidates.map(candidate => ({
    'Name': `${candidate.firstName} ${candidate.lastName}`, 'Email': candidate.email, 'Phone': candidate.phone,
    'Nationality': candidate.nationality, 'Date of Birth': candidate.dateOfBirth, 'Applied Position': candidate.appliedPosition,
    'Applied Date': candidate.appliedDate, 'Source': candidate.source, 'Experience (Years)': candidate.experience,
    'Education': candidate.education, 'Certificates': candidate.certificates.join(', '), 'Languages': candidate.languages.join(', '),
    'Previous Company': candidate.previousCompany, 'Expected Salary': candidate.expectedSalary, 'Available From': candidate.availableFrom,
    'Status': candidate.status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
    'Assigned Manager': candidate.assignedManagerName, 'Manager Feedback': candidate.managerFeedback,
    'Total Rating': candidate.totalRating, 'Assessment Points': candidate.rating.length, 'Notes': candidate.notes,
    'Created Date': new Date(candidate.createdAt).toLocaleDateString(), 'Updated Date': new Date(candidate.updatedAt).toLocaleDateString(),
  }));

  const handleExport = () => exportToExcel(buildExportData(), `candidates_${new Date().toISOString().split('T')[0]}`);
  const handlePrint = () => printAsExcel(buildExportData(), 'Candidate Applications');
  const handleAdd = () => { setEditingCandidate(undefined); setShowForm(true); };
  const handleEdit = (candidate: Candidate) => { setEditingCandidate(candidate); setShowForm(true); };
  const handleViewDetails = (candidate: Candidate) => { setSelectedCandidate(candidate); setShowDetailsModal(true); };

  const handleDelete = async (candidate: Candidate) => {
    if (!window.confirm(`Are you sure you want to delete the application from "${candidate.firstName} ${candidate.lastName}"?`)) return;
    if (!candidate?.id) return;
    try { await deleteMutation.execute(candidate); } catch (error) { console.error('Delete failed:', error); }
  };

  const handleFormSubmit = async (candidateData: Partial<Candidate>) => {
    try {
      if (editingCandidate?.id) {
        const updateData = { ...candidateData, documentId: editingCandidate.documentId };
        await updateMutation.execute({ id: editingCandidate.id, data: updateData });
      } else {
        await createMutation.execute(candidateData);
      }
    } catch (error) { console.error('Form submission failed:', error); }
  };

  const handleFormCancel = () => { setShowForm(false); setEditingCandidate(undefined); };

  const clearFilters = () => {
    setSearchTerm(''); setPositionFilter('all'); setStatusFilter('all'); setSourceFilter('all');
    setNationalityFilter('all'); setExperienceFilter('all'); setRatingFilter('all');
    setDateFromFilter(''); setDateToFilter('');
  };

  const newCandidates = filteredCandidates.filter(c => c.status === 'new');
  const underReview = filteredCandidates.filter(c => c.status === 'under_review');
  const interviewed = filteredCandidates.filter(c => c.status === 'interviewed' || c.status === 'interview_scheduled');
  const hired = filteredCandidates.filter(c => c.status === 'hired');

  const columns = getCandidateColumns(handleViewDetails);

  const filters: FilterConfig[] = [
    { key: 'position', label: 'Position', type: 'select', value: positionFilter, onChange: setPositionFilter,
      options: [{ value: 'all', label: 'All Positions' }, ...uniquePositions.map(p => ({ value: p, label: p }))] },
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter,
      options: [{ value: 'all', label: 'All Statuses' }, { value: 'new', label: 'New' }, { value: 'under_review', label: 'Under Review' },
        { value: 'interview_scheduled', label: 'Interview Scheduled' }, { value: 'interviewed', label: 'Interviewed' },
        { value: 'hired', label: 'Hired' }, { value: 'rejected', label: 'Rejected' }] },
    { key: 'source', label: 'Source', type: 'select', value: sourceFilter, onChange: setSourceFilter,
      options: [{ value: 'all', label: 'All Sources' }, { value: 'website', label: 'Website' }, { value: 'linkedin', label: 'LinkedIn' },
        { value: 'referral', label: 'Referral' }, { value: 'job_board', label: 'Job Board' }] },
    { key: 'nationality', label: 'Nationality', type: 'select', value: nationalityFilter, onChange: setNationalityFilter,
      options: [{ value: 'all', label: 'All Nationalities' }, ...uniqueNationalities.map(n => ({ value: n, label: n }))] },
    { key: 'experience', label: 'Experience', type: 'select', value: experienceFilter, onChange: setExperienceFilter,
      options: [{ value: 'all', label: 'All' }, { value: '0-2', label: '0-2 years' }, { value: '3-5', label: '3-5 years' },
        { value: '6-10', label: '6-10 years' }, { value: '10+', label: '10+ years' }] },
    { key: 'rating', label: 'Rating', type: 'select', value: ratingFilter, onChange: setRatingFilter,
      options: [{ value: 'all', label: 'All Ratings' }, { value: '0', label: 'Not Rated' }, { value: '1-3', label: '1-3 Points' },
        { value: '4-6', label: '4-6 Points' }, { value: '7-8', label: '7-8 Points' }, { value: '9-10', label: '9-10 Points' }] },
    { key: 'dateFrom', label: 'Applied From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Applied To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
  ];

  const stats = [
    { label: 'New Applications', value: newCandidates.length, color: 'gray' as const },
    { label: 'Under Review', value: underReview.length, color: 'yellow' as const },
    { label: 'Interviewed', value: interviewed.length, color: 'blue' as const },
    { label: 'Hired', value: hired.length, color: 'green' as const },
  ];

  if (isLoading) return <TableSkeleton />;
  if (error) return <ErrorState message="Error loading candidates. Please try again." onRetry={() => refetch()} entityName="candidates" />;

  return (
    <div className="space-y-6">
      <PageHeader title="Candidate Applications" description="Manage job applications and candidate evaluations from website and other sources." />
      <StatsCards stats={stats} />

      {!showForm && (
        <>
          <FilterBar
            searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search candidates..."
            filters={filters} onClearFilters={clearFilters}
            totalCount={candidates.length} filteredCount={filteredCandidates.length} entityName="Candidate Applications"
            onAdd={handleAdd} addLabel="Add Candidate" onExport={handleExport} onPrint={handlePrint}
          />
          <Table data={filteredCandidates} columns={columns} onEdit={handleEdit} onDelete={handleDelete} showAddButton={false} searchable={false} />
        </>
      )}

      {showForm && (
        <CandidateForm candidate={editingCandidate} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
      )}

      {showDetailsModal && selectedCandidate && (
        <CandidateDetailsModal
          candidate={selectedCandidate}
          onClose={() => setShowDetailsModal(false)}
          onUpdate={async (updatedCandidate) => {
            try {
              await updateMutation.execute({ id: updatedCandidate.id, data: updatedCandidate });
              setShowDetailsModal(false);
            } catch (error) { console.error('Update failed:', error); }
          }}
        />
      )}
    </div>
  );
};

export default Candidates;
